/**
 * HttpAgent that can re-attach to an in-flight server-side run.
 *
 * Live runs go through `POST {url}` as usual. Calling `enableReplayForNextRun()`
 * arms the agent so the next `runAgent()` call hits `GET {url}/stream`
 * instead — typically driven by the assistant-ui runtime's resume flow.
 * Events flow back through the runtime's normal subscriber pipeline, so
 * messages, tool calls, and state updates render exactly as if the run
 * had been initiated locally.
 */
import {
  HttpAgent,
  type AgentStateMutation,
  type AgentSubscriber,
  type HttpAgentConfig,
  type RunAgentInput,
  type RunAgentParameters,
  type RunAgentResult,
} from "@ag-ui/client";
import { EMPTY, type Observable } from "rxjs";

export class ReplayHttpAgent extends HttpAgent {
  private replayMode = false;
  /**
   * Snapshot of the live POST URL captured once at construction.
   * `runAgent` swaps `this.url` to `${liveUrl}/stream` for the duration
   * of a replay run; if we instead read `this.url` at swap time, an
   * overlapping mount (e.g. React strict mode) can capture the already-
   * swapped value and append `/stream` twice.
   */
  private readonly liveUrl: string;

  constructor(config: HttpAgentConfig) {
    super(config);
    this.liveUrl = this.url;
  }

  /** Arm the agent so the next `runAgent()` call hits the replay endpoint. */
  enableReplayForNextRun(): void {
    this.replayMode = true;
  }

  /** True while a replay run is in flight. */
  get isReplaying(): boolean {
    return this.replayMode;
  }

  protected requestInit(input: RunAgentInput): RequestInit {
    if (!this.replayMode) {
      return super.requestInit(input);
    }
    return {
      method: "GET",
      headers: { ...this.headers, Accept: "text/event-stream" },
      signal: this.abortController.signal,
    };
  }

  async runAgent(
    parameters?: RunAgentParameters,
    subscriber?: AgentSubscriber
  ): Promise<RunAgentResult> {
    if (!this.replayMode) {
      return super.runAgent(parameters, subscriber);
    }
    this.url = `${this.liveUrl}/stream`;
    try {
      return await super.runAgent(parameters, subscriber);
    } finally {
      this.url = this.liveUrl;
      this.replayMode = false;
    }
  }

  /**
   * AG-UI's built-in abort filter only matches a fixed allowlist of error
   * shapes ("AbortError", "Fetch is aborted", ...). Browser-specific stream
   * errors like Chrome's "BodyStreamBuffer was aborted" TypeError fall
   * through and surface as `Agent execution failed:` console noise on
   * navigation/unmount. When our own abort signal has fired, the run is
   * over by definition — drop the error silently.
   */
  protected onError(
    input: RunAgentInput,
    error: Error,
    subscribers: AgentSubscriber[]
  ): Observable<AgentStateMutation> {
    if (this.abortController.signal.aborted) {
      return EMPTY;
    }
    return super.onError(input, error, subscribers);
  }
}
